/**
 * Shared helpers for Common Balance Pool INPUT columns (Step 1).
 *
 * Extracted so both the builder view (which renders the inputs grid and removes inputs) and the
 * Add-input modal (which merges edited inputs back in) key inputs the same way.
 */
import { CommonBalancePoolColumn } from 'src/models/multidimensional-allocation/CommonBalancePool';
import { InputRow } from 'src/services/multidimensional-allocation/common-balance-pool/CommonBalancePoolBuilderState';

// InputRow is owned by the service-layer builder state; re-export it here so existing component
// imports (`import { InputRow } from './cbpInputs'`) keep working without reaching into services.
export type { InputRow };

export const SOURCE_TYPE_LABEL: Record<string, string> = { SEGMENT_CDT: 'Segment CDT', MDA_OUTPUT: 'MDA output' };

/** Stable grouping key for an INPUT column — its source type + the physical table/sheet id. */
export const sourceKeyOf = (col: CommonBalancePoolColumn): string =>
    `${col.sourceType || ''}|${col.sourceRef?.cdtTableId || col.sourceRef?.mdaSheetId || ''}`;

/**
 * Group a pool's INPUT columns into one row per source object.
 *
 * `resolveSourceName` resolves a SEGMENT_CDT column's table id to a human name — the backend stores only
 * the id on `sourceRef` (no name), so on reload we look it up from the live segment-CDT list; without it
 * the grid would show a raw UUID. It does NOT resolve MDA_OUTPUT sheet ids: an MDA output relies on the
 * `mdaSheetName` captured at add-time and falls back to the sheet id on reload until a builder-side
 * MDA-sheet lookup is added. Falls back to any name on the ref, then the id.
 */
export function toInputRows(
    columns: CommonBalancePoolColumn[] = [],
    resolveSourceName?: (col: CommonBalancePoolColumn) => string | undefined,
): InputRow[] {
    const groups = new Map<string, InputRow>();
    columns
        .filter((col) => col.kind === 'INPUT')
        .forEach((col) => {
            const key = sourceKeyOf(col);
            const source =
                resolveSourceName?.(col) ||
                col.sourceRef?.cdtTableName ||
                col.sourceRef?.mdaSheetName ||
                col.sourceRef?.cdtTableId ||
                col.sourceRef?.mdaSheetId ||
                '(unspecified source)';
            if (!groups.has(key)) {
                groups.set(key, { num: groups.size + 1, sourceType: col.sourceType, sourceObject: source, sourceKey: key, columns: [], cols: [] });
            }
            const group = groups.get(key)!;
            group.columns.push(col.displayName || col.columnId);
            group.cols.push(col);
        });
    return Array.from(groups.values());
}
